import type { AgentDto, EndpointDto, Role } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { usePaged } from '@/lib/queries';

function NewEndpointDialog({ tenantId, agents }: { tenantId: string; agents: AgentDto[] }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [agentId, setAgentId] = useState('');
  const create = useMutation({
    mutationFn: () => api<EndpointDto>('/endpoints', { method: 'POST', body: { agentId } }),
    onSuccess: (endpoint) => {
      toast.success(`Endpoint created for ${endpoint.agentName}`);
      void queryClient.invalidateQueries({ queryKey: ['endpoints', tenantId] });
      setOpen(false);
      void navigate({ to: '/endpoints/$endpointId', params: { endpointId: endpoint.id } });
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setAgentId('');
          create.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button disabled={agents.length === 0}>New endpoint</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Expose an agent</DialogTitle>
          <DialogDescription>
            Clients call the endpoint’s URL with an API key. It always runs the agent’s published
            version.
          </DialogDescription>
        </DialogHeader>
        <ErrorAlert error={create.error} />
        <div className="grid gap-2">
          <Label htmlFor="endpoint-agent">Agent</Label>
          <Select value={agentId} onValueChange={setAgentId}>
            <SelectTrigger id="endpoint-agent" aria-label="Agent">
              <SelectValue placeholder="Choose an agent" />
            </SelectTrigger>
            <SelectContent>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                  {a.publishedVersion ? '' : ' (not published yet)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button onClick={() => create.mutate()} disabled={!agentId || create.isPending}>
            Create endpoint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EndpointsContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  const endpoints = usePaged<EndpointDto>(['endpoints', tenantId], '/endpoints');
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const rows = endpoints.data?.pages.flatMap((p) => p.items) ?? [];
  const search = useListSearch(rows, (e) => [e.agentName, e.agentSlug, e.publicId, e.url]);
  const exposed = new Set(rows.map((e) => e.agentId));
  const available = (agents.data?.pages.flatMap((p) => p.items) ?? []).filter(
    (a) => !exposed.has(a.id),
  );

  return (
    <>
      <PageHeader
        title="Endpoints"
        description="Each endpoint is a URL that third-party clients call with an API key."
        actions={canEdit && <NewEndpointDialog tenantId={tenantId} agents={available} />}
      />
      <ErrorAlert error={endpoints.error} />
      {!endpoints.isPending && rows.length === 0 ? (
        <EmptyState title="No endpoints yet">
          {canEdit
            ? 'Publish an agent, then create an endpoint for it.'
            : 'An admin has not exposed any agents yet.'}
        </EmptyState>
      ) : (
        <div>
          <ListSearch
            className="mb-4"
            value={search.query}
            onChange={search.setQuery}
            label="Search endpoints"
            placeholder="Search by agent, public ID or URL"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Mapping</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>
                    <Link
                      to="/endpoints/$endpointId"
                      params={{ endpointId: e.id }}
                      className="font-medium hover:underline"
                    >
                      {e.agentName}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.url}</TableCell>
                  <TableCell className="text-sm">
                    {e.mapping.rules.length} rule{e.mapping.rules.length === 1 ? '' : 's'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={e.enabled ? 'secondary' : 'outline'}>
                      {e.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && search.filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <NoMatches query={search.query} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {endpoints.hasNextPage && (
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => endpoints.fetchNextPage()}
          disabled={endpoints.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
      <p className="text-muted-foreground mt-4 text-sm">
        Clients need an API key — create one on the{' '}
        <Link to="/api-keys" className="underline">
          API keys
        </Link>{' '}
        page.
      </p>
    </>
  );
}

export function EndpointsPage() {
  return (
    <RequireTenant>
      {(tenant) => <EndpointsContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
